import React, { ComponentPropsWithRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NoStylesButton from 'components/NoStylesButton';
import Tooltip from 'components/Tooltip';
import Icon from 'components/Icon';

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'bordered' && prop !== 'selected',
})<{ selected: boolean; bordered?: boolean; disabled?: boolean }>(
  ({ selected, theme, bordered, disabled }) => ({
    width: '100%',
    height: '100%',
    borderRadius: '4px',
    overflow: 'hidden',
    img: {
      height: '100%',
      objectFit: 'cover',
      verticalAlign: 'middle',
    },
    '&:hover': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
    },
    ...(bordered && {
      boxShadow: `0 0 0 1px ${theme.palette.divider}`,
    }),
    ...(selected && {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
    }),
    ...(disabled && {
      cursor: 'default',
      button: {
        pointerEvents: 'none',
        opacity: 0.2,
      },
    }),
  })
);

const Control = styled(NoStylesButton)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
  fontSize: theme.typography.pxToRem(32),
  '&:focus-visible:after': {
    content: '""',
    position: 'absolute',
    display: 'block',
    width: '100%',
    height: '100%',
    boxShadow: `${alpha(theme.palette.primary.main, 1)} 0px 50px 100px -20px, ${alpha(
      theme.palette.primary.main,
      1
    )} 0px 30px 60px -30px, ${alpha(theme.palette.primary.main, 1)} 0px 0px 6px 0px inset`,
  },
}));

type BackgroundSelectControlProps = ComponentPropsWithRef<'button'> & {
  children: React.ReactNode;
  selected: boolean;
  containerClassName?: string;
  bordered?: boolean;
  disabled?: boolean;
  tooltipText?: string;
  onDelete?: () => void;
  isCustom?: boolean;
};

const BackgroundTile = React.forwardRef<HTMLButtonElement, BackgroundSelectControlProps>(
  (
    {
      selected,
      children,
      bordered,
      containerClassName,
      tooltipText,
      disabled,
      onDelete,
      isCustom = false,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('settings');
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Box
        sx={{
          position: 'relative',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid="background-tile-container"
      >
        <Root
          selected={selected}
          bordered={bordered}
          className={containerClassName}
          disabled={disabled}
        >
          <Tooltip placement="top" title={tooltipText}>
            <span>
              <Control type="button" {...props} disabled={disabled} ref={ref}>
                {children}
              </Control>
            </span>
          </Tooltip>
        </Root>
        {isCustom && isHovered && onDelete && (
          <IconButton
            size="small"
            onClick={onDelete}
            aria-label={t('virtual_bg.delete_custom_bg')}
            sx={(theme) => ({
              position: 'absolute',
              bottom: 2,
              right: 2,
              backgroundColor: alpha(theme.palette.common.black, 0.7),
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.black, 0.8),
              },
            })}
          >
            <Icon name="trash-delete" sx={{ fontSize: 28 }} />
          </IconButton>
        )}
      </Box>
    );
  }
);

BackgroundTile.displayName = 'BackgroundTile';

export default BackgroundTile;
